import React from "react";
import type { PendingTaktification } from "../../../models";
import { toHex7 } from "../../../utils/color";
import CodePill from "../../shared/CodePill/CodePill";
import "./workCodesShared.css";
import "./CodeEditModal.css";

/** One already-taktified activity carrying this work code. */
export interface CodeEditActivity {
  id: number;
  task_code: string;
  task_name: string;
  takt_a: string;
  takt_b: string;
  takt_c: string;
}

interface Props {
  /** The committed work code being edited (Takt_Work) — the stable identity. */
  code: string;
  /** The code value to show/edit, including any staged re-code. Defaults to
   *  `code` when nothing is staged. */
  displayCode?: string;
  /** Display name (Takt_Prefix) for the header. */
  codeName: string;
  color: string;
  /** Every actual activity in the section carrying this code, with any staged
   *  draft zone already overlaid onto takt_a/b/c. */
  activities: CodeEditActivity[];
  /** Re-stage the section's zones for this code. `managedIds` is every
   *  activity the modal owns (so the handler can drop stale stagings);
   *  `taktifications` are only the rows whose zone differs from the DB. */
  onSave: (managedIds: number[], taktifications: PendingTaktification[]) => void;
  onClose: () => void;
  /** Rename / recolor / re-code this code for the section (section-local fork).
   *  `newCode` rewrites the Takt_Work value itself. */
  onEdit?: (patch: { label?: string; color?: string; newCode?: string }) => void;
  /** Delete this code from the section (full-untag its carriers). */
  onDelete?: () => void;
  /** Remove one activity from this code (full-untag it, keep the activity). */
  onRemoveActivity?: (id: number) => void;
}

interface ZoneBuffer {
  takt_b: string;
  takt_c: string;
}

/** Per-code zone editor reached from a legend chip's pencil. Lists the code's
 *  actual activities (same row shape as the Edit Takt modal) and lets you
 *  re-zone each within this code's context. Dates and the code itself are
 *  untouched — this only rewrites Takt_B / Takt_C. */
const CodeEditModal: React.FC<Props> = ({
  code,
  displayCode,
  codeName,
  color,
  activities,
  onSave,
  onClose,
  onEdit,
  onDelete,
  onRemoveActivity,
}) => {
  const origCode = displayCode ?? code;
  const [buffer, setBuffer] = React.useState<Record<number, ZoneBuffer>>(() => {
    const init: Record<number, ZoneBuffer> = {};
    for (const a of activities) {
      init[a.id] = { takt_b: a.takt_b, takt_c: a.takt_c };
    }
    return init;
  });
  const [codeBuf, setCodeBuf] = React.useState(origCode);
  const [name, setName] = React.useState(codeName);
  const [colorBuf, setColorBuf] = React.useState(() => toHex7(color));

  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const setZone = (id: number, patch: Partial<ZoneBuffer>) =>
    setBuffer((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const save = () => {
    const taktifications: PendingTaktification[] = [];
    for (const a of activities) {
      const buf = buffer[a.id] ?? { takt_b: a.takt_b, takt_c: a.takt_c };
      const b = buf.takt_b.trim();
      const c = buf.takt_c.trim();
      if (b === a.takt_b.trim() && c === a.takt_c.trim()) continue;
      taktifications.push({
        activity_id: a.id,
        takt_a: a.takt_a.trim() || null,
        takt_b: b || null,
        takt_c: c || null,
        takt_work: code,
      });
    }
    onSave(
      activities.map((a) => a.id),
      taktifications,
    );
    if (onEdit) {
      const patch: { label?: string; color?: string; newCode?: string } = {};
      // A blanked name is "no rename", not a rename to empty — staging "" would
      // show a blank chip but publish (bool("")) drops it, so the name would
      // silently revert. Only stage a real, changed name.
      if (name.trim() && name.trim() !== codeName) patch.label = name.trim();
      if (colorBuf.toLowerCase() !== toHex7(color).toLowerCase()) patch.color = colorBuf;
      // A blank code is "no change"; otherwise stage old→new relative to the
      // committed code (typing the original back clears a prior re-code).
      const nextCode = codeBuf.trim();
      if (nextCode && nextCode !== origCode) patch.newCode = nextCode;
      if (Object.keys(patch).length > 0) onEdit(patch);
    }
    onClose();
  };

  return (
    <div className="work-codes-modal-overlay" onClick={onClose}>
      <div
        className="work-codes-modal code-edit-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={`Edit work code ${code}`}
      >
        <div className="work-codes-modal-header code-edit-modal-header">
          <CodePill code={codeBuf.trim() || code} color={colorBuf} className="code-edit-modal-pill" />
          <input
            className="code-edit-modal-code"
            value={codeBuf}
            onChange={(e) => setCodeBuf(e.target.value)}
            aria-label="Work code value"
            placeholder="code"
          />
          <input
            className="code-edit-modal-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            aria-label="Work code name"
            placeholder="name"
          />
          <input
            type="color"
            className="code-edit-modal-color"
            value={colorBuf}
            onChange={(e) => setColorBuf(e.target.value)}
            aria-label="Work code color"
            title="Color"
          />
          <span className="work-codes-count">({activities.length})</span>
          {onDelete && (
            <button
              type="button"
              className="code-edit-modal-delete"
              onClick={() => {
                onDelete();
                onClose();
              }}
            >
              Delete code
            </button>
          )}
        </div>

        <div className="work-codes-modal-body work-codes-modal-body-single">
          <section className="work-codes-modal-col">
            <div className="work-codes-modal-col-scroll">
              {activities.length === 0 ? (
                <div className="work-codes-empty">
                  No activities carry this work code.
                </div>
              ) : (
                <div className="work-codes-bucket-card-activities">
                  {activities.map((a) => (
                    <div key={a.id} className="work-codes-bucket-card-activity">
                      <div className="work-codes-bucket-card-activity-line">
                        <span className="work-codes-activity-code">{a.task_code}</span>
                        <span className="work-codes-activity-title">{a.task_name}</span>
                        {onRemoveActivity && (
                          <button
                            type="button"
                            className="code-edit-modal-remove-activity"
                            title="Remove from work code"
                            aria-label="Remove from work code"
                            onClick={() => onRemoveActivity(a.id)}
                          >
                            ✕
                          </button>
                        )}
                      </div>
                      <div className="work-codes-bucket-card-zone-inputs">
                        <label className="work-codes-zone-input-label">B</label>
                        <input
                          type="text"
                          className="work-codes-zone-input"
                          value={buffer[a.id]?.takt_b ?? ""}
                          onChange={(e) => setZone(a.id, { takt_b: e.target.value })}
                          placeholder="zone"
                        />
                        <label className="work-codes-zone-input-label">C</label>
                        <input
                          type="text"
                          className="work-codes-zone-input"
                          value={buffer[a.id]?.takt_c ?? ""}
                          onChange={(e) => setZone(a.id, { takt_c: e.target.value })}
                          placeholder="level"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        </div>

        <div className="work-codes-modal-footer">
          <button type="button" className="work-codes-modal-cancel" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="work-codes-modal-save" onClick={save}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default CodeEditModal;
